/**
 * RFP Upload Modal
 * Upload RFP/RFSO documents for template generation
 */

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Modal, Button, Card, Badge, getEntityCountsBatch, getArtifactChunks, listArtifacts, deleteArtifact } from '@tracekit/core';
import { Upload, FileText, X, CheckCircle, AlertCircle, Loader2, Search, Brain, Zap, StopCircle, RefreshCw, Circle } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAPIClient } from '@tracekit/core';
import { useRFPJobStream } from '../../hooks/useRFPJobStream';
import { DocumentList } from '../RFP/DocumentList';

interface RFPUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  opportunityId: string;
  projectId: string;
  onUploadComplete: (artifactIds: string[], jobId: string) => void;
  onCachedTemplateLoad?: (template: any) => void;  // NEW: Callback for cached template
  isGeneratingTemplate?: boolean;
  templateStream?: {
    isStreaming: boolean;
    stage: string;
    message: string;
    elapsed?: number;
    progress?: number;
    cancelStream?: () => void;
  };
  disableCacheCheck?: boolean;  // NEW: Disable cache check (for ProposalBuilder flow)
  /** V3 opportunity extraction-status (from GET …/extraction-status); optional banner when extracting */
  v3StructureExtraction?: {
    status: string;
    isExtracting: boolean;
    isLoading: boolean;
    errorMessage: string | null;
  };
}

interface UploadedDocument {
  artifact_id: string;
  filename: string;
  file_size: number;
  uploaded_at: string;
  processing_status: string;
  chunks_count?: number;
  sections_extracted?: number;
  entities_count?: number;
  extracted_structure?: {
    sections: Array<{ number: string; name: string; level?: number; subsections?: any[] }>;
    mandatory_criteria: Array<{ id: string; name: string; points?: number }>;
    rated_criteria: Array<{ id: string; name: string; points?: number }>;
  };
}

/** Monotonic rank for proposal-template SSE stages (vertical checklist). */
const TEMPLATE_STREAM_STAGE_RANK: Record<string, number> = {
  error: -1,
  idle: 0,
  started: 5,
  task_started: 5,
  document_parsing: 12,
  generating: 10,
  structure_extraction: 22,
  structure_extracted: 30,
  structure_extraction_complete: 38,
  preprocessing: 14,
  preprocessing_complete: 16,
  extracting: 22,
  awaiting_approval: 42,
  template_generation: 50,
  retrieval: 52,
  retrieval_complete: 55,
  section_mapping: 60,
  question_generation: 65,
  keyword_extraction: 72,
  stats_analysis: 73,
  compliance_matrix_extraction: 74,
  parsing: 82,
  fallback: 86,
  complete: 100,
};

const VERTICAL_TEMPLATE_STEPS: { min: number; label: string }[] = [
  { min: 0, label: 'Starting job & reading RFP documents' },
  { min: 12, label: 'Extracting structure (sections, requirements, criteria)' },
  { min: 40, label: 'Continuing to full template & questions (automatic)' },
  { min: 48, label: 'Core template generation (RAG + LLM)' },
  { min: 53, label: 'Searching company & project evidence' },
  { min: 60, label: 'Mapping sections and outline' },
  { min: 65, label: 'Drafting questions by section' },
  { min: 72, label: 'Keywords, statistics & compliance' },
  { min: 80, label: 'Assembling final template' },
  { min: 99, label: 'Finished' },
];

function templateStreamRank(stage: string | undefined): number {
  if (!stage) return 0;
  if (stage === 'complete') return 100;
  return TEMPLATE_STREAM_STAGE_RANK[stage] ?? 0;
}

function verticalTemplateStepStates(stage: string | undefined): Array<'done' | 'active' | 'pending'> {
  if (stage === 'error') {
    return VERTICAL_TEMPLATE_STEPS.map(() => 'pending');
  }
  if (stage === 'complete') {
    return VERTICAL_TEMPLATE_STEPS.map(() => 'done');
  }
  const rank = templateStreamRank(stage);
  return VERTICAL_TEMPLATE_STEPS.map((row, i) => {
    const upper = VERTICAL_TEMPLATE_STEPS[i + 1]?.min ?? 101;
    if (rank >= upper) return 'done';
    if (rank >= row.min) return 'active';
    return 'pending';
  });
}

/** Bar width from stage (ignore misleading worker progress=100 at structure gate). */
function templateStreamBarPercent(stage: string | undefined): number {
  const r = templateStreamRank(stage);
  return Math.min(100, Math.max(6, Math.round(r)));
}

/** Matches list API + ingest reality: completed/failed terminal state, or has indexed chunks / entities. */
function isRfpDocReadyForTemplateList(doc: UploadedDocument): boolean {
  return (
    doc.processing_status === 'completed' ||
    doc.processing_status === 'failed' ||
    doc.processing_status === 'error' ||
    (doc.chunks_count != null && doc.chunks_count > 0) ||
    (doc.entities_count != null && doc.entities_count > 0)
  );
}

export function RFPUploadModal({
  isOpen,
  onClose,
  opportunityId,
  projectId,
  onUploadComplete,
  onCachedTemplateLoad,
  isGeneratingTemplate: externalIsGeneratingTemplate = false,
  templateStream,
  disableCacheCheck = false,
  v3StructureExtraction,
}: RFPUploadModalProps) {
  const [isGeneratingTemplate, setIsGeneratingTemplate] = useState(false);
  const isGenerating = externalIsGeneratingTemplate || isGeneratingTemplate || (templateStream?.isStreaming ?? false);
  const [files, setFiles] = useState<File[]>([]);
  const [duplicateFiles, setDuplicateFiles] = useState<string[]>([]);
  /** Files that exist in the opportunity but have 0 chunks / stuck — shown separately so user knows they'll be re-queued. */
  const [stuckRequeueFiles, setStuckRequeueFiles] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [uploadedJobId, setUploadedJobId] = useState<string | null>(null);
  const [uploadedArtifactIds, setUploadedArtifactIds] = useState<string[]>([]);
  const [uploadedDocumentsInfo, setUploadedDocumentsInfo] = useState<UploadedDocument[]>([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showAddMoreDropzone, setShowAddMoreDropzone] = useState(false); // When true, show dropzone below documents list for adding more
  const [allArtifactsForTemplate, setAllArtifactsForTemplate] = useState<UploadedDocument[]>([]);
  const [selectedExistingDocs, setSelectedExistingDocs] = useState<string[]>([]); // NEW: Track selected existing docs
  /** Artifact IDs mid-delete (slow API). Hides them from "Previously uploaded" until DELETE+refetch finish; polling can't resurrect ghosts. */
  const [pendingDeletedArtifactIds, setPendingDeletedArtifactIds] = useState<string[]>([]);
  const userHasRemovedDocumentRef = useRef(false); // When true, don't let existingDocuments effect overwrite list
  /** Timestamps of SSE reconnect attempts per job (avoids tight loops if the server keeps closing the stream). */
  const jobStreamReconnectLogRef = useRef<Record<string, number[]>>({});
  /** User clicked “Cancel” on the live stream — do not auto-reconnect SSE. */
  const userStoppedJobMonitorRef = useRef(false);
  /** SSE reported job complete — stop reconnecting and let list poll catch up. */
  const jobReportedCompleteRef = useRef(false);
  /** State mirror of the ref so the UI re-renders when the job finishes. */
  const [jobDone, setJobDone] = useState(false);
  /** Re-extract runs against existing docs without entering the upload-confirmation branch. */
  const [activeReextractArtifactId, setActiveReextractArtifactId] = useState<string | null>(null);
  /** Last good SSE payload — keep visible while fetch/SSE reconnects (avoids blanking detailed progress). */
  const lastJobStreamSnapshotRef = useRef<{
    message: string;
    detail: string | null;
    progress: number;
    stage: string;
    currentArtifact: string | null;
    artifactsProcessed: number | null;
    totalArtifacts: number | null;
    elapsed: number;
  } | null>(null);
  const queryClient = useQueryClient();

  // RFP job streaming for monitoring processing
  const rfpJobStream = useRFPJobStream();

  // Check for cached template (disabled when used from ProposalBuilder)
  const { data: cachedTemplate, refetch: refetchCachedTemplate } = useQuery({
    queryKey: ['cached-template', opportunityId],
    queryFn: async () => {
      try {
        const api = getAPIClient();
        const url = `/api/opportunities/${opportunityId}/proposal-template/cache`;
        const data = await api.get(url, {
          params: { project_id: projectId },
        });
        return data;
      } catch (error: any) {
        // No cache or error - return null
        console.log('[RFPUploadModal] No cached template found:', error.message);
        return null;
      }
    },
    enabled: isOpen && !!opportunityId && !!projectId && !disableCacheCheck,
    staleTime: 0,
    refetchOnMount: true,
  });

  // Fetch existing RFP documents - MUST be defined before useEffects that use refetchDocuments
  const {
    data: existingDocuments,
    refetch: refetchDocuments,
    isLoading: isLoadingDocs,
    isFetching: isFetchingDocs,
  } = useQuery({
    queryKey: ['rfp-documents', opportunityId, projectId],
    queryFn: async () => {
      console.log('[RFPUploadModal] ===== STARTING API FETCH =====');
      console.log('[RFPUploadModal] Params:', { opportunityId, projectId, isOpen });

      let api;
      try {
        console.log('[RFPUploadModal] Step 1: Getting API client...');
        api = getAPIClient();
        console.log('[RFPUploadModal] Step 2: API client obtained successfully');
      } catch (clientError) {
        console.error('[RFPUploadModal] FATAL: Failed to get API client:', clientError);
        console.error('[RFPUploadModal] Client error details:', {
          name: (clientError as Error).name,
          message: (clientError as Error).message,
          stack: (clientError as Error).stack
        });
        return { documents: [], total: 0 } as { documents: UploadedDocument[]; total: number };
      }

      try {
        console.log('[RFPUploadModal] Step 3: Making API request...');
        const url = `/api/opportunities/${opportunityId}/rfp-documents`;

        // APIClient.get() returns DATA directly (unwrapped), not axios response!
        const data = await api.get(url, {
          params: { project_id: projectId },
        });

        console.log('[RFPUploadModal] Step 4: Data received!');
        console.log('[RFPUploadModal] FULL DATA:', data);
        console.log('[RFPUploadModal] Data type:', typeof data);
        console.log('[RFPUploadModal] Data keys:', data ? Object.keys(data) : 'null');
        console.log('[RFPUploadModal] Has documents:', !!data?.documents);
        console.log('[RFPUploadModal] Documents length:', data?.documents?.length);

        if (!data) {
          console.error('[RFPUploadModal] No data returned!');
          return { documents: [], total: 0 } as { documents: UploadedDocument[]; total: number };
        }

        // Extract documents array
        const documents = Array.isArray(data.documents) ? data.documents : [];
        const total = typeof data.total === 'number' ? data.total : documents.length;

        const result = {
          documents,
          total
        } as { documents: UploadedDocument[]; total: number };

        console.log('[RFPUploadModal] ✅ SUCCESS! Returning:', {
          documentsCount: result.documents.length,
          total: result.total,
          firstDoc: result.documents[0]
        });

        return result;
      } catch (error) {
        console.error('[RFPUploadModal] ===== API REQUEST FAILED =====');
        console.error('[RFPUploadModal] Error:', error);
        console.error('[RFPUploadModal] Error details:', {
          name: (error as Error).name,
          message: (error as Error).message,
          response: (error as any).response,
          responseData: (error as any).response?.data
        });
        return { documents: [], total: 0 } as { documents: UploadedDocument[]; total: number };
      }
    },
    enabled: isOpen && !!opportunityId && !!projectId,
    staleTime: 0,
    refetchOnMount: true,
    // Poll every 5 seconds while any document is still processing — catches the case where
    // the SSE stream drops mid-job and the completion event is never received by the modal.
    refetchInterval: (data) => {
      const docs = (data as any)?.documents as UploadedDocument[] | undefined;
      const s = (d: UploadedDocument) => (d.processing_status || '').toLowerCase();
      const hasPending = docs?.some((d) => {
        if (['queued', 'running', 'pending', 'ingested'].includes(s(d))) return true;
        if (s(d) === 'failed' || s(d) === 'completed') return false;
        const chunks = d.chunks_count ?? 0;
        const ents = d.entities_count ?? 0;
        return chunks === 0 && ents === 0;
      });
      return hasPending ? 5000 : false;
    },
  });

  const rfpDocsQueryKey = ['rfp-documents', opportunityId, projectId] as const;

  const [reextractingArtifactId, setReextractingArtifactId] = useState<string | null>(null);

  const handleReIngestAllStuck = useCallback(
    async (stuckArtifactIds: string[]) => {
      if (!opportunityId || !projectId || stuckArtifactIds.length === 0) return;
      try {
        const api = getAPIClient();
        const data = await api.post<{
          job_id?: string;
          artifact_ids?: string[];
          status?: string;
          message?: string;
        }>(
          `/api/opportunities/${opportunityId}/rfp-documents/re-ingest-stuck`,
          {},
          { params: { project_id: projectId } },
        );
        const jobId = data?.job_id;
        if (!jobId) {
          console.warn('[RFPUploadModal] Bulk re-ingest: no job_id returned (may already be clean)', data);
          void refetchDocuments?.();
          return;
        }
        setJobDone(false);
        jobReportedCompleteRef.current = false;
        userStoppedJobMonitorRef.current = false;

        const markQueued = (docs: UploadedDocument[]) =>
          docs.map((d) =>
            stuckArtifactIds.includes(String(d.artifact_id))
              ? { ...d, processing_status: 'queued', chunks_count: 0, sections_extracted: 0, entities_count: 0, extracted_structure: undefined }
              : d,
          );
        setUploadedDocumentsInfo((docs) => (docs.length ? markQueued(docs) : docs));
        setAllArtifactsForTemplate((docs) => (docs.length ? markQueued(docs) : docs));
        setUploadedArtifactIds((prev) => {
          const newIds = stuckArtifactIds.filter((id) => !prev.includes(id));
          return newIds.length ? [...prev, ...newIds] : prev;
        });

        setUploadedJobId(jobId);
        rfpJobStream.reset();
        rfpJobStream.startStream(opportunityId, jobId, projectId);
        void refetchDocuments?.();
        void queryClient.invalidateQueries({ queryKey: rfpDocsQueryKey });
      } catch (err: unknown) {
        const anyErr = err as { response?: { data?: { detail?: string } }; message?: string };
        const msg = anyErr?.response?.data?.detail || anyErr?.message || 'Failed to queue bulk re-ingest.';
        console.error('[RFPUploadModal] Bulk re-ingest failed:', err);
        alert(typeof msg === 'string' ? msg : JSON.stringify(msg));
      }
    },
    [opportunityId, projectId, rfpJobStream, refetchDocuments, queryClient, rfpDocsQueryKey],
  );

  const handleReextractStructure = useCallback(
    async (artifactId: string) => {
      const idStr = String(artifactId);
      if (!opportunityId || !projectId) {
        alert('Missing opportunity or project — cannot re-ingest.');
        return;
      }
      setReextractingArtifactId(idStr);
      try {
        const api = getAPIClient();
        const data = await api.post<{
          job_id?: string;
          artifact_ids?: string[];
          status?: string;
          message?: string;
        }>(
          `/api/opportunities/${opportunityId}/rfp-documents/${idStr}/re-ingest`,
          {},
          { params: { project_id: projectId } },
        );
        const jobId = data?.job_id;
        if (!jobId) {
          console.error('[RFPUploadModal] Re-ingest response missing job_id:', data);
          alert('Re-ingest was accepted but no job id was returned. Check the document list in a moment.');
          return;
        }

        setJobDone(false);
        setActiveReextractArtifactId(idStr);
        jobReportedCompleteRef.current = false;
        userStoppedJobMonitorRef.current = false;

        const mergedList =
          allArtifactsForTemplate.length > 0 ? allArtifactsForTemplate : uploadedDocumentsInfo;
        const allIds = mergedList.map((d) => String(d.artifact_id));
        setUploadedArtifactIds((prev) => {
          if (prev.length === 0 && allIds.length > 0) return allIds;
          return prev.includes(idStr) ? prev : [...prev, idStr];
        });

        const markQueued = (docs: UploadedDocument[]) =>
          docs.map((d) =>
            String(d.artifact_id) === idStr
              ? {
                  ...d,
                  processing_status: 'queued',
                  extracted_structure: undefined,
                  chunks_count: 0,
                  sections_extracted: 0,
                  entities_count: 0,
                }
              : d,
          );
        setUploadedDocumentsInfo((docs) => (docs.length ? markQueued(docs) : docs));
        setAllArtifactsForTemplate((docs) => (docs.length ? markQueued(docs) : docs));

        setUploadedJobId(jobId);
        rfpJobStream.reset();
        rfpJobStream.startStream(opportunityId, jobId, projectId);

        void refetchDocuments?.();
        void queryClient.invalidateQueries({ queryKey: rfpDocsQueryKey });
      } catch (err: unknown) {
        const anyErr = err as { response?: { data?: { detail?: string } }; message?: string };
        const msg =
          anyErr?.response?.data?.detail ||
          anyErr?.message ||
          'Failed to queue re-ingest. Please try again.';
        console.error('[RFPUploadModal] Re-extract / re-ingest failed:', err);
        alert(typeof msg === 'string' ? msg : JSON.stringify(msg));
      } finally {
        setReextractingArtifactId(null);
      }
    },
    [
      opportunityId,
      projectId,
      allArtifactsForTemplate,
      uploadedDocumentsInfo,
      queryClient,
      rfpJobStream,
      refetchDocuments,
    ],
  );

  /** Server list minus rows pending delete — fixes UI showing files until slow DELETE completes or stale poll returns. */
  const existingDocumentsForUI = useMemo(() => {
    if (!existingDocuments?.documents) return existingDocuments;
    if (pendingDeletedArtifactIds.length === 0) return existingDocuments;
    const pending = new Set(pendingDeletedArtifactIds.map(String));
    const documents = existingDocuments.documents.filter((d) => !pending.has(String(d.artifact_id)));
    return { ...existingDocuments, documents, total: documents.length };
  }, [existingDocuments, pendingDeletedArtifactIds]);

  // Debug log to check query state
  console.log('[RFPUploadModal] Query state:', {
    isOpen,
    opportunityId,
    projectId,
    enabled: isOpen && !!opportunityId && !!projectId,
    isLoadingDocs,
    isFetchingDocs,
    hasData: !!existingDocuments,
    documentsCount: existingDocuments?.documents?.length || 0,
    showConfirmation
  });

  // When job completes, show confirmation step - DO NOT auto-trigger template generation
  // Template generation is ONLY triggered when user clicks "Generate Template" button
  useEffect(() => {
    if ((rfpJobStream.stage === 'complete' || rfpJobStream.stage === 'completed') && uploadedJobId && uploadedArtifactIds.length > 0 && !showConfirmation) {
      jobReportedCompleteRef.current = true;
      setJobDone(true);
      console.log('[RFPUploadModal] Job completed, showing confirmation step - waiting for user to click Generate Template');
      setShowConfirmation(true);

      // Refresh document list and fetch chunks/entities counts for uploaded artifacts
      if (refetchDocuments && uploadedArtifactIds.length > 0 && projectId) {
        refetchDocuments().then(async (response) => {
          const allDocs = response.data?.documents || [];
          let uploadedDocs = allDocs.filter((doc: UploadedDocument) =>
            uploadedArtifactIds.includes(doc.artifact_id)
          );

          // Fetch chunks and entities counts for uploaded artifacts (similar to KnowledgeBase)
          try {
            // Fetch entity counts in batch (optimized)
            const entityCounts = await getEntityCountsBatch(projectId, uploadedArtifactIds);

            // Fetch chunk counts for each artifact
            const chunksPromises = uploadedArtifactIds.map(async (artifactId) => {
              try {
                const chunksResponse = await getArtifactChunks(artifactId);
                // API returns { total_chunks, chunks, pagination } - use total_chunks for accurate count
                const totalChunks = chunksResponse?.total_chunks ?? 0;
                return { artifactId, count: totalChunks };
              } catch (error) {
                console.error(`[RFPUploadModal] Failed to fetch chunks for artifact ${artifactId}:`, error);
                return { artifactId, count: 0 };
              }
            });

            const chunksResults = await Promise.all(chunksPromises);
            const chunksCounts: Record<string, number> = {};
            chunksResults.forEach(({ artifactId, count }) => {
              chunksCounts[artifactId] = count;
            });

            // Update uploaded docs with fetched counts
            uploadedDocs = uploadedDocs.map((doc) => ({
              ...doc,
              chunks_count: chunksCounts[doc.artifact_id] ?? doc.chunks_count,
              entities_count: entityCounts[doc.artifact_id] ?? doc.entities_count,
            }));
          } catch (error) {
            console.error('[RFPUploadModal] Failed to fetch artifact counts after job completion:', error);
            // Continue with existing data if fetch fails
          }

          setUploadedDocumentsInfo(uploadedDocs);

          // Combine newly uploaded with existing documents for template generation
          setAllArtifactsForTemplate([
            ...uploadedDocs,
            ...allDocs.filter(
              (doc: UploadedDocument) =>
                !uploadedArtifactIds.includes(doc.artifact_id) && isRfpDocReadyForTemplateList(doc)
            ),
          ]);
        });
      }

      // DO NOT call onUploadComplete here - only when user clicks button
    }
  }, [rfpJobStream.stage, uploadedJobId, uploadedArtifactIds, showConfirmation, refetchDocuments]);

  // When the documents query refetches, merge fresh API fields into local lists. Previously we skipped
  // any sync after upload, so chunk counts / status stayed at the pre-ingest snapshot (0 chunks) forever.
  useEffect(() => {
    if (existingDocumentsForUI && existingDocumentsForUI.documents.length > 0) {
      const allDocs = existingDocumentsForUI.documents;

      if (userHasRemovedDocumentRef.current) {
        return;
      }

      const apiById = new Map(allDocs.map((d) => [String(d.artifact_id), d]));

      const mergeFromApi = (local: UploadedDocument): UploadedDocument => {
        const api = apiById.get(String(local.artifact_id));
        if (!api) return local;
        return {
          ...local,
          processing_status: api.processing_status,
          chunks_count: api.chunks_count ?? local.chunks_count,
          sections_extracted: api.sections_extracted ?? local.sections_extracted,
          entities_count: api.entities_count ?? local.entities_count,
          extracted_structure: api.extracted_structure ?? local.extracted_structure,
          file_size: api.file_size ?? local.file_size,
          uploaded_at: api.uploaded_at || local.uploaded_at,
          filename: api.filename || local.filename,
        };
      };

      if (showConfirmation && (uploadedDocumentsInfo.length > 0 || allArtifactsForTemplate.length > 0)) {
        // Drop rows that no longer exist on the server (e.g. after delete). Old merge kept ghosts when !api.
        setUploadedDocumentsInfo((prev) =>
          prev.length
            ? prev.filter((local) => apiById.has(String(local.artifact_id))).map(mergeFromApi)
            : prev,
        );
        setAllArtifactsForTemplate((prev) =>
          prev.length
            ? prev.filter((local) => apiById.has(String(local.artifact_id))).map(mergeFromApi)
            : prev,
        );
        return;
      }

      if (showConfirmation) {
        const uploadedDocs = uploadedArtifactIds.length > 0
          ? allDocs.filter((doc: UploadedDocument) => uploadedArtifactIds.includes(doc.artifact_id))
          : [];
        // Include ALL existing docs (ready + stuck) so users can see and re-ingest stuck ones
        const existingDocs = allDocs.filter(
          (doc: UploadedDocument) => !uploadedArtifactIds.includes(doc.artifact_id)
        );
        if (uploadedDocs.length > 0 || existingDocs.length > 0) {
          setAllArtifactsForTemplate([...uploadedDocs, ...existingDocs]);
        }
      } else if (uploadedArtifactIds.length === 0 && allDocs.length > 0) {
        // Show ALL docs (ready + stuck) so user can see full picture and re-ingest stuck ones
        if (allDocs.length > 0) {
          setAllArtifactsForTemplate(allDocs);
          setShowConfirmation(true);
        }
      }
    }
  }, [existingDocumentsForUI, uploadedArtifactIds, showConfirmation, uploadedDocumentsInfo.length, allArtifactsForTemplate.length]);

  // Reset state when modal closes or opens (ensure clean state on each open)
  useEffect(() => {
    if (!isOpen) {
      // Reset all state when modal closes
      setShowConfirmation(false);
      setUploadedDocumentsInfo([]);
      setAllArtifactsForTemplate([]);
      setUploadedArtifactIds([]);
      setUploadedJobId(null);
      setFiles([]);
      setDuplicateFiles([]);
      setStuckRequeueFiles([]);
      setIsGeneratingTemplate(false); // Reset local generating state
      setSelectedExistingDocs([]); // Reset selected existing docs
      setShowAddMoreDropzone(false);
      setPendingDeletedArtifactIds([]);
      userHasRemovedDocumentRef.current = false;
      jobStreamReconnectLogRef.current = {};
      userStoppedJobMonitorRef.current = false;
    } else {
      // When modal opens, ensure generating state is reset (in case it was left in a bad state)
      if (!externalIsGeneratingTemplate && !templateStream?.isStreaming) {
        setIsGeneratingTemplate(false);
      }
    }
  }, [isOpen, externalIsGeneratingTemplate, templateStream?.isStreaming]);

  // DEBUG: Log state changes
  useEffect(() => {
    console.log('[RFPUploadModal] State update:', {
      showConfirmation,
      uploadedDocumentsInfo: uploadedDocumentsInfo.length,
      allArtifactsForTemplate: allArtifactsForTemplate.length,
      uploadedArtifactIds: uploadedArtifactIds.length,
      artifacts: uploadedDocumentsInfo.length > 0 ? uploadedDocumentsInfo : allArtifactsForTemplate
    });
  }, [showConfirmation, uploadedDocumentsInfo, allArtifactsForTemplate, uploadedArtifactIds]);

  // Ingest/index readiness for template — do NOT use sections_extracted alone: structure can be populated while
  // processing_status is still "queued" and chunks/entities are 0, which wrongly hid the job-progress panel.
  const isDocumentReadyForTemplate = isRfpDocReadyForTemplateList;
  const artifactsListForTemplate = allArtifactsForTemplate.length > 0 ? allArtifactsForTemplate : uploadedDocumentsInfo;
  const rfpStreamErrorText = `${rfpJobStream.error || ''} ${rfpJobStream.message || ''}`.toLowerCase();
  const reextractMonitorDisconnected =
    !!activeReextractArtifactId &&
    rfpJobStream.stage === 'error' &&
    /network|fetch|connection|stream|aborted|terminated|timeout/.test(rfpStreamErrorText);
  const activeReextractActuallyFailed =
    !!activeReextractArtifactId &&
    (rfpJobStream.stage === 'failed' || (rfpJobStream.stage === 'error' && !reextractMonitorDisconnected));
  const activeReextractIsTerminal =
    !!activeReextractArtifactId &&
    (rfpJobStream.stage === 'complete' ||
      rfpJobStream.stage === 'completed' ||
      activeReextractActuallyFailed);
  const isActivelyReextractingDoc = (doc: UploadedDocument) =>
    !!activeReextractArtifactId &&
    !activeReextractIsTerminal &&
    String(doc.artifact_id) === activeReextractArtifactId;
  const completedArtifactsForTemplate = artifactsListForTemplate.filter(
    (doc) => !isActivelyReextractingDoc(doc) && isDocumentReadyForTemplate(doc)
  );

  /** Every document currently in the template list is ingest-ready (scalable for N docs, not “first wins”). */
  const allDocumentsReadyForTemplate =
    artifactsListForTemplate.length > 0 &&
    completedArtifactsForTemplate.length === artifactsListForTemplate.length;

  /** At least one doc is ready - Generate Template can proceed with whatever is ready (stuck docs do not block). */
  const someDocumentsReadyForTemplate = completedArtifactsForTemplate.length > 0;

  /** True when every artifact from the last upload response has real data (even if SSE lied or ended early). */
  const uploadedArtifactsReadyViaApi = useMemo(() => {
    if (uploadedArtifactIds.length === 0) return false;
    const findDoc = (id: string) =>
      allArtifactsForTemplate.find((d) => String(d.artifact_id) === id) ||
      uploadedDocumentsInfo.find((d) => String(d.artifact_id) === id);
    return uploadedArtifactIds.every((id) => {
      const doc = findDoc(String(id));
      return doc != null && isDocumentReadyForTemplate(doc);
    });
  }, [uploadedArtifactIds, allArtifactsForTemplate, uploadedDocumentsInfo]);

  /** User can generate when at least one doc is ready OR when the job is done (stuck docs do not block). */
  const documentsReadyForTemplate =
    artifactsListForTemplate.length > 0 && (someDocumentsReadyForTemplate || jobDone);

  /** Show ingest UX only while the job is still running AND no docs are ready yet. */
  const showIngestProgressPanel =
    showConfirmation && artifactsListForTemplate.length > 0 && !someDocumentsReadyForTemplate && !jobDone;
  const activeReextractDoc = useMemo(() => {
    if (!activeReextractArtifactId) return null;
    const docs = [
      ...(existingDocumentsForUI?.documents || []),
      ...uploadedDocumentsInfo,
      ...allArtifactsForTemplate,
    ];
    return docs.find((d) => String(d.artifact_id) === activeReextractArtifactId) || null;
  }, [activeReextractArtifactId, existingDocumentsForUI, uploadedDocumentsInfo, allArtifactsForTemplate]);
  const showReextractProgressPanel = !!activeReextractArtifactId;

  useEffect(() => {
    if (!activeReextractArtifactId) return;
    if (
      rfpJobStream.stage !== 'complete' &&
      rfpJobStream.stage !== 'completed' &&
      !activeReextractActuallyFailed
    ) {
      return;
    }

    void refetchDocuments?.();
    void queryClient.invalidateQueries({ queryKey: rfpDocsQueryKey });

    const timer = window.setTimeout(() => {
      setActiveReextractArtifactId(null);
      setUploadedJobId(null);
      setJobDone(false);
      jobReportedCompleteRef.current = false;
      rfpJobStream.reset();
    }, activeReextractActuallyFailed ? 8000 : 5000);

    return () => clearTimeout(timer);
  }, [
    activeReextractArtifactId,
    rfpJobStream.stage,
    rfpJobStream.reset,
    activeReextractActuallyFailed,
    refetchDocuments,
    queryClient,
    rfpDocsQueryKey,
  ]);

  useEffect(() => {
    if (!reextractMonitorDisconnected) return;
    void refetchDocuments?.();
    void queryClient.invalidateQueries({ queryKey: rfpDocsQueryKey });
  }, [reextractMonitorDisconnected, refetchDocuments, queryClient, rfpDocsQueryKey]);

  // Once all documents are ready via the list API, clean up the SSE monitor so the UI exits the
  // progress panel.  This fires regardless of whether SSE said "complete" first — the list API is
  // the authoritative source of readiness.
  // Guard: do NOT fire while a re-extract is in progress — its own cleanup effect owns the stream
  // lifecycle.  If this fires while activeReextractArtifactId is set, it would call rfpJobStream.reset()
  // prematurely, causing the re-extract panel to flash "Stage: idle" before the green banner shows.
  useEffect(() => {
    if (!uploadedJobId || !showConfirmation || uploadedArtifactIds.length === 0) return;
    if (activeReextractArtifactId) return;
    if (!uploadedArtifactsReadyViaApi) return;
    if (!allDocumentsReadyForTemplate) return;

    console.log(
      '[RFPUploadModal] All documents ready per API — clearing job monitor.'
    );
    jobReportedCompleteRef.current = true;
    setJobDone(true);
    rfpJobStream.reset();
    setUploadedJobId(null);
  }, [
    uploadedJobId,
    showConfirmation,
    uploadedArtifactIds,
    activeReextractArtifactId,
    uploadedArtifactsReadyViaApi,
    allDocumentsReadyForTemplate,
    rfpJobStream.reset,
  ]);

  // Safety valve: if jobDone but allDocumentsReadyForTemplate is somehow still false after 15s,
  // force a final refetch. The panel is already hidden by jobDone, so this just ensures data is fresh.
  useEffect(() => {
    if (!jobDone || allDocumentsReadyForTemplate || !showConfirmation) return;
    const timer = window.setTimeout(() => {
      console.log('[RFPUploadModal] Safety valve: final refetch after job done');
      rfpJobStream.reset();
      setUploadedJobId(null);
      refetchDocuments?.();
    }, 15_000);
    return () => clearTimeout(timer);
  }, [jobDone, showConfirmation, allDocumentsReadyForTemplate, rfpJobStream.reset, refetchDocuments]);

  // If the job stream ends while documents are still pending, re-attach SSE — but ONLY if the
  // job hasn't already reported complete.  Once the backend says "complete", there's no more SSE
  // data to collect; we just let the list poll catch up with the final artifact states.
  useEffect(() => {
    if (!isOpen || (!showConfirmation && !activeReextractArtifactId) || !uploadedJobId || !opportunityId || !projectId) return;
    if (userStoppedJobMonitorRef.current) return;
    if (allDocumentsReadyForTemplate && !activeReextractArtifactId) return;
    if (rfpJobStream.isStreaming) return;
    if (rfpJobStream.stage === 'error' && !reextractMonitorDisconnected) return;

    // Once SSE reported the job finished, don't reconnect — just force a list refetch instead.
    if (
      jobReportedCompleteRef.current ||
      rfpJobStream.stage === 'complete' ||
      rfpJobStream.stage === 'completed'
    ) {
      console.log('[RFPUploadModal] Job already complete — refreshing document list instead of reconnecting SSE');
      jobReportedCompleteRef.current = true;
      setJobDone(true);
      refetchDocuments?.();
      return;
    }

    const now = Date.now();
    const log = jobStreamReconnectLogRef.current[uploadedJobId] || [];
    const recent = log.filter((t) => now - t < 60_000);
    if (recent.length >= 4) {
      console.warn('[RFPUploadModal] SSE reconnect limit reached for job', uploadedJobId);
      jobReportedCompleteRef.current = true;
      setJobDone(true);
      refetchDocuments?.();
      return;
    }

    const timer = window.setTimeout(() => {
      if (userStoppedJobMonitorRef.current || jobReportedCompleteRef.current) return;
      jobStreamReconnectLogRef.current[uploadedJobId] = [...recent, Date.now()];
      console.log('[RFPUploadModal] Re-attaching RFP ingest job SSE (pending docs, stream inactive)');
      rfpJobStream.startStream(opportunityId, uploadedJobId, projectId);
    }, 2000);

    return () => clearTimeout(timer);
  }, [
    isOpen,
    showConfirmation,
    activeReextractArtifactId,
    uploadedJobId,
    opportunityId,
    projectId,
    allDocumentsReadyForTemplate,
    rfpJobStream.isStreaming,
    rfpJobStream.stage,
    rfpJobStream.startStream,
    reextractMonitorDisconnected,
    refetchDocuments,
  ]);

  // Preserve last SSE frame so reconnect gaps don't wipe detailed message/detail/file name.
  // Also mark the job as "reported complete" the moment the stream says so.
  useEffect(() => {
    if (
      rfpJobStream.isStreaming &&
      (rfpJobStream.message || rfpJobStream.detail || rfpJobStream.currentArtifact)
    ) {
      lastJobStreamSnapshotRef.current = {
        message: rfpJobStream.message,
        detail: rfpJobStream.detail,
        progress: rfpJobStream.progress,
        stage: rfpJobStream.stage,
        currentArtifact: rfpJobStream.currentArtifact,
        artifactsProcessed: rfpJobStream.artifactsProcessed,
        totalArtifacts: rfpJobStream.totalArtifacts,
        elapsed: rfpJobStream.elapsed,
      };
    }
    if (rfpJobStream.stage === 'complete' || rfpJobStream.stage === 'completed') {
      jobReportedCompleteRef.current = true;
      setJobDone(true);
    }
  }, [
    rfpJobStream.isStreaming,
    rfpJobStream.message,
    rfpJobStream.detail,
    rfpJobStream.progress,
    rfpJobStream.stage,
    rfpJobStream.currentArtifact,
    rfpJobStream.artifactsProcessed,
    rfpJobStream.totalArtifacts,
    rfpJobStream.elapsed,
  ]);

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async (filesToUpload: File[]) => {
      const api = getAPIClient();
      const formData = new FormData();

      filesToUpload.forEach((file) => {
        formData.append('files', file);
      });

      // api.post already returns response.data, so we get the data object directly
      const responseData = await api.post(
        `/api/opportunities/${opportunityId}/rfp-documents/upload?project_id=${projectId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress((prev) => ({
                ...prev,
                [filesToUpload[0].name]: progress,
              }));
            }
          },
        }
      );

      console.log('[RFPUploadModal] API response data:', responseData);
      return responseData;
    },
    onSuccess: async (data) => {
      console.log('[RFPUploadModal] Upload success response:', data);
      setFiles([]);
      setUploadProgress({});

      // Validate response exists
      if (!data) {
        console.error('[RFPUploadModal] No response data received - attempting fallback lookup');
        // Try to get job ID from opportunity metadata as fallback
        try {
          const documentsResponse = await refetchDocuments();
          const documents = documentsResponse.data?.documents || [];

          if (documents.length > 0) {
            // Try to find job ID from metadata
            const api = getAPIClient();
            const oppResponse = await api.get(`/api/opportunities/${opportunityId}`, {
              params: { project_id: projectId },
            });

            const oppData = oppResponse?.data?.data || {};
            const rfpMetadata = oppData.rfp_document_metadata || {};

            // Find the most recent structure extraction job ID
            let foundJobId = '';
            for (const [artifactId, metadata] of Object.entries(rfpMetadata)) {
              if (metadata && typeof metadata === 'object') {
                // Check for structure job in opportunity metadata
                const structureJobId = (metadata as any).structure_job_id;
                if (structureJobId) {
                  foundJobId = structureJobId;
                  break;
                }
              }
            }

            if (foundJobId) {
              console.log('[RFPUploadModal] Found job ID from metadata:', foundJobId);
              const artifactIds = documents.map((d: UploadedDocument) => d.artifact_id);
              setUploadedJobId(foundJobId);
              rfpJobStream.startStream(opportunityId, foundJobId, projectId);
              onUploadComplete(artifactIds, foundJobId);
              return;
            }
          }
        } catch (fallbackError) {
          console.error('[RFPUploadModal] Fallback lookup failed:', fallbackError);
        }

        alert('Upload completed but no response data was received. The file may still be processing. Please check the documents list.');
        return;
      }

      // Handle both snake_case and camelCase response formats
      const artifactIds = data.artifact_ids || data.artifactIds || [];
      const jobId = data.job_id || data.jobId || '';
      const status = data.status || 'unknown';

      console.log('[RFPUploadModal] Parsed response:', { artifactIds, jobId, status, data });

      // Always validate artifact IDs first
      if (!artifactIds || !Array.isArray(artifactIds) || artifactIds.length === 0) {
        console.error('[RFPUploadModal] No artifact IDs in response:', data);
        alert('Upload completed but no artifact IDs were returned. Please check the console.');
        return;
      }

      // Always refetch documents to get latest status
      console.log('[RFPUploadModal] Refetching documents after upload...');
      const documentsResponse = await refetchDocuments();
      console.log('[RFPUploadModal] Documents refetch response:', {
        hasData: !!documentsResponse?.data,
        documentsCount: documentsResponse?.data?.documents?.length || 0,
        total: documentsResponse?.data?.total || 0,
        fullResponse: documentsResponse?.data
      });

      const allDocuments = documentsResponse?.data?.documents || [];
      console.log('[RFPUploadModal] All documents from refetch:', {
        count: allDocuments.length,
        artifactIds: allDocuments.map((d: UploadedDocument) => d.artifact_id)
      });

      // Get info for uploaded artifacts - try matching by artifact ID first
      let uploadedDocs = allDocuments.filter((doc: UploadedDocument) =>
        artifactIds.includes(doc.artifact_id)
      );
      console.log('[RFPUploadModal] Filtered uploaded docs by ID:', {
        count: uploadedDocs.length,
        artifactIds: uploadedDocs.map(d => d.artifact_id),
        filenames: uploadedDocs.map(d => d.filename)
      });

      // If we didn't find all artifacts in the documents list (e.g., duplicate/cached files),
      // query artifacts directly by ID to get their info
      if (uploadedDocs.length < artifactIds.length && projectId) {
        console.log('[RFPUploadModal] Some artifacts not found in documents list, querying artifacts directly...');
        try {
          // Get all artifacts for the project and match by ID
          const artifactsResponse = await listArtifacts(projectId, 1000, 0);
          const allArtifacts = artifactsResponse?.artifacts || [];
          console.log('[RFPUploadModal] All artifacts from listArtifacts:', {
            count: allArtifacts.length,
            artifactIds: allArtifacts.map(a => a.id)
          });

          // Find missing artifacts by ID
          const missingArtifactIds = artifactIds.filter(id =>
            !uploadedDocs.some(doc => doc.artifact_id === id)
          );
          console.log('[RFPUploadModal] Missing artifact IDs:', missingArtifactIds);

          for (const artifactId of missingArtifactIds) {
            const artifact = allArtifacts.find(a => a.id === artifactId);
            if (artifact) {
              console.log('[RFPUploadModal] Found missing artifact:', {
                id: artifact.id,
                filename: artifact.filename,
                sourceType: artifact.source_type
              });

              // Create UploadedDocument from Artifact
              const doc: UploadedDocument = {
                artifact_id: artifact.id,
                filename: artifact.filename || 'Unknown',
                file_size: artifact.file_size_bytes || 0,
                uploaded_at: artifact.created_at ? new Date(artifact.created_at).toISOString() : new Date().toISOString(),
                processing_status: 'completed', // Assume completed if it exists
                chunks_count: undefined, // Will be fetched below
                entities_count: undefined, // Will be fetched below
              };
              uploadedDocs.push(doc);
            } else {
              console.warn('[RFPUploadModal] Artifact not found even in full list:', artifactId);
            }
          }

          console.log('[RFPUploadModal] After querying artifacts directly:', {
            count: uploadedDocs.length,
            artifactIds: uploadedDocs.map(d => d.artifact_id),
            filenames: uploadedDocs.map(d => d.filename)
          });
        } catch (error) {
          console.error('[RFPUploadModal] Failed to query artifacts directly:', error);
        }
      }

      // Fetch chunks and entities counts for uploaded artifacts (similar to KnowledgeBase)
      if (artifactIds.length > 0 && projectId) {
        try {
          // Fetch entity counts in batch (optimized)
          const entityCounts = await getEntityCountsBatch(projectId, artifactIds);

          // Fetch chunk counts for each artifact
          const chunksPromises = artifactIds.map(async (artifactId) => {
            try {
              const chunksResponse = await getArtifactChunks(artifactId);
              // API returns { total_chunks, chunks, pagination } - use total_chunks for accurate count
              const totalChunks = (chunksResponse as any)?.total_chunks ?? (Array.isArray(chunksResponse) ? chunksResponse.length : 0);
              return { artifactId, count: totalChunks };
            } catch (error) {
              console.error(`[RFPUploadModal] Failed to fetch chunks for artifact ${artifactId}:`, error);
              return { artifactId, count: 0 };
            }
          });

          const chunksResults = await Promise.all(chunksPromises);
          const chunksCounts: Record<string, number> = {};
          chunksResults.forEach(({ artifactId, count }) => {
            chunksCounts[artifactId] = count;
          });

          // Update uploaded docs with fetched counts
          uploadedDocs = uploadedDocs.map((doc) => ({
            ...doc,
            chunks_count: chunksCounts[doc.artifact_id] ?? doc.chunks_count,
            entities_count: entityCounts[doc.artifact_id] ?? doc.entities_count,
          }));
        } catch (error) {
          console.error('[RFPUploadModal] Failed to fetch artifact counts:', error);
          // Continue with existing data if fetch fails
        }
      }

      // CRITICAL: Set artifact IDs FIRST before any other state
      // This ensures useEffect hooks don't interfere
      console.log('[RFPUploadModal] Setting state - artifactIds:', artifactIds);
      setUploadedArtifactIds(artifactIds);

      // IMPORTANT: Set state immediately so UI can show artifacts
      // Even if uploadedDocs is empty initially, set it so UI can update
      console.log('[RFPUploadModal] Setting uploadedDocumentsInfo:', {
        count: uploadedDocs.length,
        docs: uploadedDocs.map(d => ({ id: d.artifact_id, filename: d.filename, chunks: d.chunks_count, entities: d.entities_count }))
      });
      setUploadedDocumentsInfo(uploadedDocs.length > 0 ? uploadedDocs : []);

      // Combine newly uploaded with existing documents for template generation
      // This ensures we show ALL artifacts that will be used for template generation
      const existingCompletedDocs = allDocuments.filter(
        (doc: UploadedDocument) =>
          !artifactIds.includes(doc.artifact_id) && isRfpDocReadyForTemplateList(doc)
      );
      const allArtifacts = [...uploadedDocs, ...existingCompletedDocs];
      console.log('[RFPUploadModal] Setting allArtifactsForTemplate:', {
        uploaded: uploadedDocs.length,
        existing: existingCompletedDocs.length,
        total: allArtifacts.length,
        allArtifacts: allArtifacts.map(d => ({ id: d.artifact_id, filename: d.filename }))
      });
      setAllArtifactsForTemplate(allArtifacts.length > 0 ? allArtifacts : []);

      // CRITICAL: Show confirmation immediately so UI displays artifacts
      // This MUST be set regardless of whether we have artifacts yet
      // Set this LAST to ensure all other state is set first
      console.log('[RFPUploadModal] Setting showConfirmation = true');
      setShowConfirmation(true);
      setShowAddMoreDropzone(false); // Collapse "Add more" dropzone after successful upload
      userHasRemovedDocumentRef.current = false; // Allow effect to sync list again after new upload

      console.log('[RFPUploadModal] Artifacts for template:', {
        uploaded: uploadedDocs.length,
        existing: existingCompletedDocs.length,
        total: allArtifacts.length,
        artifactIds,
        showConfirmation: true,
        uploadedDocs: uploadedDocs.map(d => ({ id: d.artifact_id, filename: d.filename, chunks: d.chunks_count, entities: d.entities_count })),
        allArtifacts: allArtifacts.map(d => ({ id: d.artifact_id, filename: d.filename, chunks: d.chunks_count, entities: d.entities_count }))
      });

      // If we have a job ID, start monitoring immediately
      if (jobId && jobId.trim() !== '') {
        console.log('[RFPUploadModal] Starting job monitoring for job:', jobId);
        userStoppedJobMonitorRef.current = false;
        jobReportedCompleteRef.current = false;
        setJobDone(false);
        setUploadedJobId(jobId);
        rfpJobStream.startStream(opportunityId, jobId, projectId);
      } else {
        // If no job ID (cached file, already processed), show confirmation immediately
        console.log('[RFPUploadModal] No job ID - file may be cached/already processed, showing confirmation');
        // Still fetch counts if we haven't already
        if (allArtifacts.length > 0 && projectId) {
          // Ensure we have counts for all artifacts
          const allArtifactIds = allArtifacts.map(doc => doc.artifact_id);
          try {
            const entityCounts = await getEntityCountsBatch(projectId, allArtifactIds);
            const chunksPromises = allArtifactIds.map(async (artifactId) => {
              try {
                const chunksResponse = await getArtifactChunks(artifactId);
                // API returns { total_chunks, chunks, pagination } - use total_chunks for accurate count
                const totalChunks = chunksResponse?.total_chunks ?? 0;
                return { artifactId, count: totalChunks };
              } catch (error) {
                return { artifactId, count: 0 };
              }
            });
            const chunksResults = await Promise.all(chunksPromises);
            const chunksCounts: Record<string, number> = {};
            chunksResults.forEach(({ artifactId, count }) => {
              chunksCounts[artifactId] = count;
            });

            // Update all artifacts with counts
            const updatedArtifacts = allArtifacts.map((doc) => ({
              ...doc,
              chunks_count: chunksCounts[doc.artifact_id] ?? doc.chunks_count ?? 0,
              entities_count: entityCounts[doc.artifact_id] ?? doc.entities_count ?? 0,
            }));

            // CRITICAL: Update state atomically to prevent flickering
            setAllArtifactsForTemplate(updatedArtifacts);
            setUploadedDocumentsInfo(updatedArtifacts.filter(doc => artifactIds.includes(doc.artifact_id)));
            // Ensure confirmation stays true
            setShowConfirmation(true);

            console.log('[RFPUploadModal] Updated artifacts with counts:', {
              total: updatedArtifacts.length,
              uploaded: updatedArtifacts.filter(doc => artifactIds.includes(doc.artifact_id)).length,
              showConfirmation: true
            });
          } catch (error) {
            console.error('[RFPUploadModal] Failed to fetch counts for cached file:', error);
            // Still show confirmation even if counts fail - preserve existing state
            setShowConfirmation(true);
          }
        } else {
          // No artifacts to fetch, but still show confirmation
          setShowConfirmation(true);
        }
      }

      // Show confirmation step - user can review files before template generation
      // This happens regardless of job ID (files may already be processed)
      // Note: We already set this above, but ensure it's set for jobs too
      if (jobId && jobId.trim() !== '') {
        // For jobs, confirmation is already set above
        // The useEffect will handle updates when job completes
        console.log('[RFPUploadModal] Job ID present, confirmation already set');
      }
    },
    onError: (error: any) => {
      console.error('[RFPUploadModal] Upload failed:', error);
      console.error('[RFPUploadModal] Error response:', error.response?.data);
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Unknown error';
      alert(`Upload failed: ${errorMessage}`);
    },
    onSettled: () => {
      setUploading(false);
    },
  });

  const checkForDuplicates = (
    newFiles: File[]
  ): { unique: File[]; duplicates: string[]; stuck: string[] } => {
    const allDocs = existingDocuments?.documents || [];
    // Build lookup maps: name → ready-state
    const readyNames = new Set(
      allDocs
        .filter((d) => isRfpDocReadyForTemplateList(d))
        .map((d) => d.filename.toLowerCase().trim())
    );
    const stuckNames = new Set(
      allDocs
        .filter((d) => !isRfpDocReadyForTemplateList(d))
        .map((d) => d.filename.toLowerCase().trim())
    );
    const duplicates: string[] = [];
    const stuck: string[] = [];
    const unique: File[] = [];
    newFiles.forEach((file) => {
      const key = file.name.toLowerCase().trim();
      if (readyNames.has(key)) {
        // Truly processed — block re-upload
        duplicates.push(file.name);
      } else if (stuckNames.has(key)) {
        // Exists but not processed — allow re-upload (will re-queue ingest)
        stuck.push(file.name);
        unique.push(file);
      } else {
        unique.push(file);
      }
    });
    return { unique, duplicates, stuck };
  };
  
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    const { unique, duplicates, stuck } = checkForDuplicates(selectedFiles);
    if (duplicates.length > 0) setDuplicateFiles(duplicates);
    if (stuck.length > 0) setStuckRequeueFiles(stuck);
    setFiles((prev) => [...prev, ...unique]);
  };

  const handleRemoveFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = () => {
    if (files.length === 0) {
      alert('Please select at least one file');
      return;
    }

    // New upload in the same modal session: clear a stuck SSE / previous job id so spinner cannot persist.
    rfpJobStream.reset();
    setUploadedJobId(null);
    jobStreamReconnectLogRef.current = {};
    userStoppedJobMonitorRef.current = false;

    setUploading(true);
    uploadMutation.mutate(files);
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files).filter(
      (file) => file.type === 'application/pdf' || file.name.endsWith('.docx') || file.name.endsWith('.doc')
    );
    const { unique, duplicates, stuck } = checkForDuplicates(droppedFiles);
    if (duplicates.length > 0) setDuplicateFiles(duplicates);
    if (stuck.length > 0) setStuckRequeueFiles(stuck);
    setFiles((prev) => [...prev, ...unique]);
  }, [existingDocuments]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Upload RFP Documents"
      size="xl"
    >
      <div className="space-y-6">
        {/* Initial / browse step: show loading while fetching so "Previously uploaded" isn't a blank gap */}
        {!showConfirmation && (isLoadingDocs || isFetchingDocs) && (
          <div
            className="flex items-start gap-3 p-4 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30"
            role="status"
            aria-live="polite"
          >
            <Loader2 className="h-5 w-5 text-blue-600 dark:text-blue-400 animate-spin flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-blue-900 dark:text-blue-200">
                Loading RFP documents…
              </p>
              <p className="text-xs text-blue-800/90 dark:text-blue-200/80 mt-1">
                Fetching your uploaded files for this opportunity. Lists and counts will appear in a moment.
              </p>
            </div>
          </div>
        )}

        {v3StructureExtraction &&
          (v3StructureExtraction.isExtracting ||
            v3StructureExtraction.status === 'extracting' ||
            (v3StructureExtraction.status === 'awaiting_approval' &&
              !(disableCacheCheck && isGenerating))) && (
            <div
              className="flex items-start gap-3 p-4 rounded-lg border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-950/30"
              role="status"
              aria-live="polite"
            >
              {(v3StructureExtraction.isExtracting || v3StructureExtraction.status === 'extracting') && (
                <Loader2 className="h-5 w-5 text-indigo-600 dark:text-indigo-400 animate-spin flex-shrink-0 mt-0.5" />
              )}
              {v3StructureExtraction.status === 'awaiting_approval' && !v3StructureExtraction.isExtracting && (
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
              )}
              <div>
                <p className="text-sm font-semibold text-indigo-900 dark:text-indigo-200">
                  {v3StructureExtraction.status === 'awaiting_approval'
                    ? 'RFP structure extracted'
                    : 'Extracting RFP structure…'}
                </p>
                <p className="text-xs text-indigo-800/90 dark:text-indigo-200/80 mt-1">
                  Opportunity structure pipeline. Status:{' '}
                  <span className="font-mono">{v3StructureExtraction.status}</span>
                </p>
                {v3StructureExtraction.errorMessage && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">{v3StructureExtraction.errorMessage}</p>
                )}
              </div>
            </div>
          )}

        {showReextractProgressPanel && (
          <div
            className={`p-4 rounded-lg border-2 shadow-sm ${
              activeReextractActuallyFailed
                ? 'border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/30'
                : rfpJobStream.stage === 'complete' || rfpJobStream.stage === 'completed'
                  ? 'border-green-300 dark:border-green-800 bg-green-50 dark:bg-green-950/30'
                  : reextractMonitorDisconnected
                    ? 'border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30'
                  : 'border-blue-300 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30'
            }`}
            role="status"
            aria-live="polite"
          >
            <div className="flex items-start gap-3">
              {activeReextractActuallyFailed ? (
                <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              ) : rfpJobStream.stage === 'complete' || rfpJobStream.stage === 'completed' ? (
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
              ) : reextractMonitorDisconnected ? (
                <Loader2 className="h-5 w-5 text-amber-600 dark:text-amber-400 animate-spin flex-shrink-0 mt-0.5" />
              ) : (
                <Loader2 className="h-5 w-5 text-blue-600 dark:text-blue-400 animate-spin flex-shrink-0 mt-0.5" />
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {activeReextractActuallyFailed
                    ? 'Re-extract failed'
                    : rfpJobStream.stage === 'complete' || rfpJobStream.stage === 'completed'
                      ? 'Re-extract complete'
                      : reextractMonitorDisconnected
                        ? 'Re-extract still running — reconnecting monitor'
                      : 'Re-extracting document structure…'}
                </p>
                <p className="text-xs text-gray-700 dark:text-gray-300 mt-1">
                  {activeReextractDoc?.filename || 'Selected RFP document'} is being re-ingested, re-indexed, and
                  re-scanned for proposal structure and solicitation constraints.
                </p>
                <p className="text-xs text-gray-700 dark:text-gray-300 mt-1">
                  {reextractMonitorDisconnected
                    ? 'The browser lost the live progress connection, but the worker job may still be running. Refreshing status and reconnecting…'
                    : rfpJobStream.message || 'Queued worker job…'}
                  {rfpJobStream.detail ? ` ${rfpJobStream.detail}` : ''}
                </p>
                <div className="mt-3 h-2.5 bg-white/70 dark:bg-slate-900/50 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 transition-all duration-300"
                    style={{
                      width: `${Math.max(8, Math.min(100, rfpJobStream.progress || (rfpJobStream.stage === 'extracting' ? 80 : 12)))}%`,
                    }}
                  />
                </div>
                <div className="mt-2 flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
                  <span>Stage: {reextractMonitorDisconnected ? 'reconnecting monitor' : (rfpJobStream.stage === 'idle' ? 'queued' : rfpJobStream.stage) || 'queued'}</span>
                  {rfpJobStream.elapsed > 0 && <span>{rfpJobStream.elapsed.toFixed(1)}s elapsed</span>}
                </div>
              </div>
              {rfpJobStream.isStreaming && (
                <button
                  type="button"
                  onClick={() => {
                    userStoppedJobMonitorRef.current = true;
                    rfpJobStream.cancelStream();
                  }}
                  className="text-xs text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                >
                  Stop watching
                </button>
              )}
            </div>
          </div>
        )}

        {/* DEBUG: Log render state */}
        {(() => {
          console.log('[RFPUploadModal RENDER] Current state:', {
            isOpen,
            isLoadingDocs,
            showConfirmation,
            uploadedDocumentsInfo: uploadedDocumentsInfo.length,
            allArtifactsForTemplate: allArtifactsForTemplate.length,
            uploadedArtifactIds: uploadedArtifactIds.length,
            shouldShowArtifacts: showConfirmation && (uploadedDocumentsInfo.length > 0 || allArtifactsForTemplate.length > 0),
            existingDocuments: existingDocuments ? {
              total: existingDocuments.total,
              documentsLength: existingDocuments.documents?.length || 0
            } : null,
            uploadedDocs: uploadedDocumentsInfo.map(d => ({ id: d.artifact_id, filename: d.filename })),
            allArtifacts: allArtifactsForTemplate.map(d => ({ id: d.artifact_id, filename: d.filename }))
          });
          return null;
        })()}

        {/* Documents for Template - WITH EXPAND ARROWS + ADD MORE BUTTON */}
        {showConfirmation && (uploadedDocumentsInfo.length > 0 || allArtifactsForTemplate.length > 0) ? (
          <div className="space-y-3">
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              {(() => {
                const listDocs = allArtifactsForTemplate.length > 0 ? allArtifactsForTemplate : uploadedDocumentsInfo;
                const stuckDocs = listDocs.filter(
                  (d) => !isRfpDocReadyForTemplateList(d) && (d.chunks_count == null || d.chunks_count === 0)
                );
                const readyDocs = listDocs.filter((d) => isRfpDocReadyForTemplateList(d));
                return (
                  <>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-200 flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        Documents for Template ({listDocs.length})
                        {stuckDocs.length > 0 && (
                          <span className="text-xs font-normal text-orange-600 dark:text-orange-400">
                            · {readyDocs.length} ready, {stuckDocs.length} need re-ingest
                          </span>
                        )}
                      </h3>
                      <div className="flex items-center gap-2">
                        {stuckDocs.length > 0 && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleReIngestAllStuck(stuckDocs.map((d) => String(d.artifact_id)))}
                            className="border-orange-300 text-orange-700 hover:bg-orange-50 dark:border-orange-700 dark:text-orange-300 dark:hover:bg-orange-900/20"
                          >
                            <RefreshCw className="h-3 w-3 mr-1" />
                            Re-ingest {stuckDocs.length} stuck
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setShowAddMoreDropzone((prev) => !prev)}
                        >
                          <Upload className="h-3 w-3 mr-1" />
                          {showAddMoreDropzone ? 'Hide upload area' : 'Add More Documents'}
                        </Button>
                      </div>
                    </div>
                  </>
                );
              })()}

              <DocumentList
                documents={allArtifactsForTemplate.length > 0 ? allArtifactsForTemplate : uploadedDocumentsInfo}
                selectedDocs={[]}  // All docs in this section are already selected for template generation
                onRemove={async (docId) => {
                  const idStr = String(docId);
                  // Block the existingDocuments sync effect from re-applying stale rows while DELETE runs / refetch lags.
                  userHasRemovedDocumentRef.current = true;
                  // Immediate UI + cache: DELETE can take 30–60s; polling may still return the old row until server finishes.
                  setPendingDeletedArtifactIds((prev) => (prev.includes(idStr) ? prev : [...prev, idStr]));
                  setSelectedExistingDocs((prev) => prev.filter((x) => String(x) !== idStr));
                  queryClient.setQueryData<{ documents: UploadedDocument[]; total: number }>(
                    rfpDocsQueryKey,
                    (old) => {
                      if (!old?.documents) return old;
                      const documents = old.documents.filter((d) => String(d.artifact_id) !== idStr);
                      return { ...old, documents, total: documents.length };
                    },
                  );
                  const nextUploaded = uploadedDocumentsInfo.filter((d) => String(d.artifact_id) !== idStr);
                  const nextAll = allArtifactsForTemplate.filter((d) => String(d.artifact_id) !== idStr);
                  const willBeEmpty = nextUploaded.length === 0 && nextAll.length === 0;
                  // Optimistic UI update
                  setUploadedDocumentsInfo(nextUploaded);
                  setAllArtifactsForTemplate(nextAll);
                  const wasInUploadBatch = uploadedArtifactIds.some((id) => String(id) === idStr);
                  // Last document removed: return to initial upload step (no "Generate Template" with 0 files).
                  if (willBeEmpty) {
                    setUploadedArtifactIds([]);
                    setShowConfirmation(false);
                    setJobDone(false);
                    jobReportedCompleteRef.current = false;
                    setUploadedJobId(null);
                    rfpJobStream.reset();
                    setShowAddMoreDropzone(false);
                    setFiles([]);
                  } else {
                    setUploadedArtifactIds((prev) => prev.filter((id) => String(id) !== idStr));
                  }
                  if (wasInUploadBatch && !willBeEmpty) {
                    rfpJobStream.reset();
                    setUploadedJobId(null);
                  }
                  try {
                    await deleteArtifact(idStr);
                  } catch (err: any) {
                    const status = err?.response?.status ?? err?.status;
                    // Already gone — keep UI consistent with first click
                    if (status === 404) {
                      console.info('[RFPUploadModal] Artifact already deleted (404), refreshing list');
                    } else {
                      console.error('[RFPUploadModal] Failed to delete artifact from backend:', err);
                    }
                  } finally {
                    try {
                      await refetchDocuments();
                    } finally {
                      queryClient.invalidateQueries({ queryKey: rfpDocsQueryKey });
                      setPendingDeletedArtifactIds((prev) => prev.filter((x) => x !== idStr));
                      userHasRemovedDocumentRef.current = false;
                    }
                  }
                }}
                onRefresh={() => refetchDocuments()}
                onReextractStructure={handleReextractStructure}
                reextractingArtifactId={activeReextractArtifactId || reextractingArtifactId}
              />

              {showAddMoreDropzone && (
                <div
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  className="mt-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center hover:border-blue-400 dark:hover:border-blue-600 transition-colors bg-white/50 dark:bg-gray-900/30"
                >
                  <Upload className="h-10 w-10 mx-auto text-gray-400 mb-3" />
                  <p className="text-sm text-gray-600 dark:text-theme-text-secondary mb-3">
                    Drag and drop more RFP documents here, or click to browse
                  </p>
                  <input
                    type="file"
                    accept=".pdf,.docx,.doc"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                    id="rfp-file-input-add-more"
                  />
                  <label
                    htmlFor="rfp-file-input-add-more"
                    className="inline-block px-4 py-2 bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700 transition-colors"
                  >
                    Select Files
                  </label>
                  <p className="text-xs text-gray-500 dark:text-theme-text-secondary mt-2">
                    Supported: PDF, DOCX (max 50MB per file)
                  </p>
                </div>
              )}

              {!documentsReadyForTemplate && artifactsListForTemplate.length > 0 && (
                <p className="mt-2 text-xs text-amber-700 dark:text-amber-300 flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin flex-shrink-0" />
                  Generate template will be available when all documents show as completed.
                </p>
              )}
            </div>
          </div>
        ) : showConfirmation && (isLoadingDocs || isFetchingDocs) ? (
          <div
            className="flex items-start gap-3 p-6 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30"
            role="status"
            aria-live="polite"
          >
            <Loader2 className="h-8 w-8 text-blue-600 dark:text-blue-400 animate-spin flex-shrink-0" />
            <div>
              <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-200">
                Loading RFP document details…
              </h3>
              <p className="text-sm text-blue-800/90 dark:text-blue-200/80 mt-1">
                Refreshing chunks, sections, and entities from the server. Your files are still there—this
                usually takes only a few seconds.
              </p>
            </div>
          </div>
        ) : (
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center hover:border-blue-400 dark:hover:border-blue-600 transition-colors"
          >
            <Upload className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <p className="text-sm text-gray-600 dark:text-theme-text-secondary mb-4">
              Drag and drop RFP/RFSO documents here, or click to browse
            </p>
            <input
              type="file"
              accept=".pdf,.docx,.doc"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              id="rfp-file-input"
            />
            <label
              htmlFor="rfp-file-input"
              className="inline-block px-4 py-2 bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700 transition-colors"
            >
              Select Files
            </label>
            <p className="text-xs text-gray-500 dark:text-theme-text-secondary mt-2">
              Supported: PDF, DOCX (max 50MB per file)
            </p>
          </div>
        )}
        
        {(duplicateFiles.length > 0 || stuckRequeueFiles.length > 0) && (
  <div className="mb-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
    <div className="flex items-start gap-2">
      <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
      <div className="flex-1">
        {duplicateFiles.length > 0 && (
          <>
            <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
              {duplicateFiles.length === 1 ? 'File already uploaded' : 'Files already uploaded'}
            </p>
            <ul className="mt-1 space-y-0.5">
              {duplicateFiles.map((name) => (
                <li key={name} className="text-xs text-amber-700 dark:text-amber-300 flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" />
                  <span>{name} — already processed, skipping</span>
                </li>
              ))}
            </ul>
          </>
        )}
        {stuckRequeueFiles.length > 0 && (
          <>
            <p className={`text-sm font-medium text-blue-800 dark:text-blue-200 ${duplicateFiles.length > 0 ? 'mt-2' : ''}`}>
              {stuckRequeueFiles.length === 1 ? 'File needs re-processing' : 'Files need re-processing'} — will be re-queued
            </p>
            <ul className="mt-1 space-y-0.5">
              {stuckRequeueFiles.map((name) => (
                <li key={name} className="text-xs text-blue-700 dark:text-blue-300 flex items-center gap-1">
                  <RefreshCw className="h-3 w-3" />
                  <span>{name} — queuing new ingest job</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
      <button
        onClick={() => { setDuplicateFiles([]); setStuckRequeueFiles([]); }}
        className="text-amber-500 hover:text-amber-700"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  </div>
)}
        {/* Selected Files */}
        {files.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-700 dark:text-theme-text">
              Selected Files ({files.length})
            </h3>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {files.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded"
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <span className="text-sm text-gray-700 dark:text-theme-text truncate">
                      {file.name}
                    </span>
                    <span className="text-xs text-gray-500">
                      ({(file.size / 1024 / 1024).toFixed(2)} MB)
                    </span>
                  </div>
                  {uploadProgress[file.name] !== undefined ? (
                    <div className="text-xs text-blue-600">
                      {uploadProgress[file.name]}%
                    </div>
                  ) : (
                    <button
                      onClick={() => handleRemoveFile(index)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Previously Uploaded Documents - WITH EXPAND ARROWS FOR STRUCTURE */}
        {existingDocumentsForUI && existingDocumentsForUI.documents && existingDocumentsForUI.documents.length > 0 && !showConfirmation && (
          <div className="space-y-3 mt-4 border-t border-gray-200 dark:border-gray-700 pt-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-theme-text">
                Previously Uploaded RFP Documents ({existingDocumentsForUI.total})
              </h3>
              {existingDocumentsForUI.documents.length > 0 && (
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onClick={() => setSelectedExistingDocs(existingDocumentsForUI.documents.map(d => d.artifact_id))}>
                    Select All
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setSelectedExistingDocs([])}>
                    Clear
                  </Button>
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Select documents to include. Click expand arrow to see extracted structure.
            </p>

            <DocumentList
              documents={existingDocumentsForUI.documents}
              selectedDocs={selectedExistingDocs}
              onToggleSelect={(docId) => {
                if (selectedExistingDocs.includes(docId)) {
                  setSelectedExistingDocs(selectedExistingDocs.filter(id => id !== docId));
                } else {
                  setSelectedExistingDocs([...selectedExistingDocs, docId]);
                }
              }}
              onRefresh={() => refetchDocuments()}
              onReextractStructure={handleReextractStructure}
              reextractingArtifactId={activeReextractArtifactId || reextractingArtifactId}
            />

            {selectedExistingDocs.length > 0 && (
              <Button
                variant="primary"
                className="w-full"
                onClick={async () => {
                  const selectedDocs = existingDocumentsForUI.documents.filter(d => selectedExistingDocs.includes(d.artifact_id));
                  setUploadedDocumentsInfo(selectedDocs);
                  setAllArtifactsForTemplate(selectedDocs);
                  // Docs that have no chunks yet need to be ingested before template generation can run.
                  // Reuse the same re-ingest path as "Re-extract structure" so the job id is tracked
                  // and the SSE stream starts — avoiding the "No job id on this session" dead-end.
                  const unreadyDocs = selectedDocs.filter(d => !isDocumentReadyForTemplate(d));
                  for (const doc of unreadyDocs) {
                    await handleReextractStructure(String(doc.artifact_id));
                  }
                  setShowConfirmation(true);
                }}
              >
                Use Selected Documents ({selectedExistingDocs.length})
              </Button>
            )}
          </div>
        )}

        {/* Template generation: vertical pipeline (matches ProposalBuilder semantics; not the old 5-dot funnel). */}
        {isGenerating && templateStream && (() => {
          const stage = templateStream.stage;
          const barPct = templateStreamBarPercent(stage);
          const rowStates = verticalTemplateStepStates(stage);
          const statusMessage =
            stage === 'awaiting_approval'
              ? 'Structure extracted — continuing automatically to template & questions…'
              : templateStream.message || 'Working on your proposal template…';
          return (
          <div className="p-4 bg-gradient-to-r from-blue-50 via-indigo-50 to-blue-50 dark:from-blue-950/40 dark:via-indigo-950/40 dark:to-blue-950/40 border-2 border-blue-200 dark:border-blue-800 rounded-lg shadow-lg">
            <div className="flex items-center justify-between mb-3 pb-2 border-b border-blue-200 dark:border-blue-800">
              <div className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                <span className="text-xs font-medium text-blue-900 dark:text-blue-200">
                  Proposal template generation
                </span>
              </div>
              {templateStream.elapsed !== undefined && (
                <div className="text-xs text-blue-600 dark:text-blue-400 font-mono">
                  {templateStream.elapsed.toFixed(1)}s elapsed
                </div>
              )}
            </div>

            <div className="flex items-center gap-3 mb-3">
              <div className="relative">
                {stage === 'started' || stage === 'task_started' ? (
                  <Loader2 className="h-5 w-5 text-blue-600 dark:text-blue-400 animate-spin" />
                ) : stage === 'template_generation' || stage === 'generating' ? (
                  <Brain className="h-5 w-5 text-blue-600 dark:text-blue-400 animate-pulse" />
                ) : stage === 'retrieval' || stage === 'retrieval_complete' ? (
                  <Search className="h-5 w-5 text-blue-600 dark:text-blue-400 animate-pulse" />
                ) : stage === 'parsing' || stage === 'structure_extraction' || stage === 'structure_extracted' ? (
                  <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400 animate-bounce" />
                ) : stage === 'complete' ? (
                  <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                ) : (
                  <Loader2 className="h-5 w-5 text-blue-600 dark:text-blue-400 animate-spin" />
                )}
                {stage !== 'complete' && stage !== 'error' && (
                  <span className="absolute -top-1 -right-1 flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-500" />
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1">
                  {statusMessage}
                </div>
                <div className="text-xs text-blue-800 dark:text-blue-200/90">
                  Stage:{' '}
                  <span className="font-mono bg-blue-100 dark:bg-blue-900/60 px-1 rounded">{stage || 'processing'}</span>
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{barPct}%</div>
                <div className="text-xs text-blue-500 dark:text-blue-400">overall</div>
              </div>
            </div>

            <div className="space-y-1 mb-4">
              <div className="h-3 bg-blue-200 dark:bg-blue-900 rounded-full overflow-hidden shadow-inner">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 via-indigo-600 to-blue-600 transition-all duration-500 relative"
                  style={{ width: `${barPct}%` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-40 animate-shimmer" />
                </div>
              </div>
            </div>

            <div className="border-t border-blue-200 dark:border-blue-800 pt-3">
              <p className="text-xs font-semibold text-blue-900 dark:text-blue-200 mb-2">Pipeline</p>
              <ol className="space-y-2">
                {VERTICAL_TEMPLATE_STEPS.map((step, idx) => {
                  const st = rowStates[idx];
                  return (
                    <li key={step.label} className="flex items-start gap-2 text-xs">
                      {st === 'done' ? (
                        <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                      ) : st === 'active' ? (
                        <Loader2 className="h-4 w-4 text-blue-600 dark:text-blue-400 animate-spin flex-shrink-0 mt-0.5" />
                      ) : (
                        <Circle className="h-4 w-4 text-slate-300 dark:text-slate-600 flex-shrink-0 mt-0.5" />
                      )}
                      <span
                        className={
                          st === 'active'
                            ? 'text-blue-900 dark:text-blue-100 font-semibold'
                            : st === 'done'
                              ? 'text-blue-800/90 dark:text-blue-200/90'
                              : 'text-slate-500 dark:text-slate-500'
                        }
                      >
                        {step.label}
                      </span>
                    </li>
                  );
                })}
              </ol>
            </div>

            {templateStream.stage !== 'complete' && templateStream.stage !== 'error' && templateStream.cancelStream && (
              <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-800 flex justify-end">
                <Button
                  onClick={() => {
                    templateStream.cancelStream?.();
                  }}
                  variant="outline"
                  size="sm"
                  className="text-blue-800 dark:text-blue-200 border-blue-300 dark:border-blue-700"
                >
                  <StopCircle className="h-3 w-3 mr-1" />
                  Cancel generation
                </Button>
              </div>
            )}
          </div>
          );
        })()}

        {/* Job processing: show while ANY listed doc is not ready — not only while SSE is connected (multi-file / duplicates). */}
        {showIngestProgressPanel && (
          <div className="p-4 bg-gradient-to-r from-blue-100 via-indigo-100 to-blue-100 dark:from-blue-900/30 dark:via-indigo-900/30 dark:to-blue-900/30 rounded-lg border-2 border-blue-300 dark:border-blue-700 shadow-lg">
            {!rfpJobStream.isStreaming ? (
              <div className="flex items-start gap-3 mb-3">
                {jobReportedCompleteRef.current ? (
                  <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                ) : (
                  <Loader2 className="h-5 w-5 text-blue-600 dark:text-blue-400 animate-spin flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-blue-900 dark:text-blue-200">
                    {jobReportedCompleteRef.current
                      ? 'Job completed — updating document list…'
                      : (lastJobStreamSnapshotRef.current?.message || 'Processing documents…')}
                  </div>
                  {!jobReportedCompleteRef.current && (lastJobStreamSnapshotRef.current?.currentArtifact ||
                    lastJobStreamSnapshotRef.current?.artifactsProcessed != null) && (
                      <div className="text-xs text-blue-800 dark:text-blue-100/90 mt-1 font-medium">
                        {lastJobStreamSnapshotRef.current?.currentArtifact
                          ? `Active file: ${lastJobStreamSnapshotRef.current.currentArtifact}`
                          : null}
                        {lastJobStreamSnapshotRef.current?.totalArtifacts != null &&
                          lastJobStreamSnapshotRef.current?.totalArtifacts! > 1 &&
                          lastJobStreamSnapshotRef.current?.artifactsProcessed != null
                          ? ` · Job file ${(lastJobStreamSnapshotRef.current.artifactsProcessed ?? 0) + 1} of ${lastJobStreamSnapshotRef.current.totalArtifacts}`
                          : null}
                      </div>
                    )}
                  {!jobReportedCompleteRef.current && lastJobStreamSnapshotRef.current?.detail ? (
                    <div className="text-xs text-blue-900/90 dark:text-blue-100/90 mt-2 leading-snug border-l-2 border-blue-400 dark:border-blue-500 pl-2">
                      {lastJobStreamSnapshotRef.current.detail}
                    </div>
                  ) : null}
                  <div className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                    {completedArtifactsForTemplate.length} of {artifactsListForTemplate.length} ready in this list.
                    {jobReportedCompleteRef.current
                      ? ' Worker finished — waiting for the document list to catch up. Click Refresh or wait a moment.'
                      : uploadedJobId
                        ? ' Reconnecting to live job progress…'
                        : ' No job id on this session — the worker may still be ingesting. Use Refresh or wait for chunk counts to update.'}
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => void refetchDocuments()}>
                  <RefreshCw className="h-3 w-3 mr-2" />
                  Refresh status
                </Button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-3">
                  <div className="relative">
                    {rfpJobStream.stage === 'parsing' ? (
                      <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400 animate-pulse" />
                    ) : rfpJobStream.stage === 'chunking' ? (
                      <Search className="h-5 w-5 text-blue-600 dark:text-blue-400 animate-bounce" />
                    ) : rfpJobStream.stage === 'indexing' ? (
                      <Zap className="h-5 w-5 text-blue-600 dark:text-blue-400 animate-spin" />
                    ) : rfpJobStream.stage === 'extracting' ? (
                      <Brain className="h-5 w-5 text-blue-600 dark:text-blue-400 animate-pulse" />
                    ) : (
                      <Loader2 className="h-5 w-5 text-blue-600 dark:text-blue-400 animate-spin" />
                    )}
                    <span className="absolute -top-1 -right-1 flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-500"></span>
                    </span>
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-1">
                      {rfpJobStream.message || 'Processing RFP document...'}
                    </div>
                    <div className="text-xs text-blue-700 dark:text-blue-300">
                      {rfpJobStream.jobType === 'ingest'
                        ? 'Ingesting document'
                        : rfpJobStream.jobType === 'rfp_structure_extract'
                          ? 'Extracting structure'
                          : 'Processing'}
                      {rfpJobStream.jobStatus && rfpJobStream.jobStatus !== 'running' ? (
                        <span className="ml-1 opacity-80">
                          ·{' '}
                          {rfpJobStream.jobStatus === 'checkpointed'
                            ? 'resume checkpoint saved (still working or continuing)'
                            : rfpJobStream.jobStatus}
                        </span>
                      ) : null}
                    </div>
                    {(rfpJobStream.currentArtifact ||
                      (rfpJobStream.totalArtifacts != null && rfpJobStream.totalArtifacts > 1)) && (
                        <div className="text-xs text-blue-800 dark:text-blue-100/90 mt-1 font-medium">
                          {rfpJobStream.currentArtifact ? `Active file: ${rfpJobStream.currentArtifact}` : null}
                          {rfpJobStream.totalArtifacts != null &&
                            rfpJobStream.totalArtifacts > 1 &&
                            rfpJobStream.artifactsProcessed != null
                            ? ` · File ${rfpJobStream.artifactsProcessed + 1} of ${rfpJobStream.totalArtifacts} (structure/ingest)`
                            : null}
                        </div>
                      )}
                    {rfpJobStream.detail ? (
                      <div className="text-xs text-blue-900/90 dark:text-blue-100/90 mt-2 leading-snug border-l-2 border-blue-400 dark:border-blue-500 pl-2">
                        {rfpJobStream.detail}
                      </div>
                    ) : null}
                  </div>
                  {rfpJobStream.elapsed > 0 && (
                    <div className="text-right">
                      <div className="text-lg font-bold text-blue-600 dark:text-blue-400">
                        {rfpJobStream.elapsed.toFixed(1)}s
                      </div>
                      <div className="text-xs text-blue-500 dark:text-blue-400">elapsed</div>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      userStoppedJobMonitorRef.current = true;
                      rfpJobStream.cancelStream();
                    }}
                    className="ml-2 flex items-center gap-1 text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                  >
                    <StopCircle className="h-4 w-4 mr-2" />
                    Cancel
                  </button>
                </div>
              </>
            )}

            {rfpJobStream.isStreaming ? (
              <>
                {/* Progress bar - use minimum visible width when progress is 0 so bar is always visible */}
                {(() => {
                  const stageOrder: Record<string, number> = {
                    queued: 5,
                    waiting: 8,
                    parsing: 20,
                    chunking: 40,
                    indexing: 60,
                    extracting: 80,
                    processing: 88,
                    complete: 100,
                    completed: 100,
                    failed: 0,
                  };
                  const rawProgress = rfpJobStream.progress ?? 0;
                  const stageFallback = stageOrder[rfpJobStream.stage] ?? 0;
                  const displayProgress =
                    rawProgress > 0 ? Math.min(100, rawProgress) : stageFallback > 0 ? stageFallback : 8;
                  const barWidth = Math.max(8, Math.min(100, displayProgress));
                  return (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-xs text-blue-700 dark:text-blue-300">
                        <span
                          className={
                            ['queued', 'waiting'].includes(rfpJobStream.stage)
                              ? 'text-blue-900 dark:text-blue-200 font-bold'
                              : rfpJobStream.stage === 'parsing'
                                ? 'text-blue-900 dark:text-blue-200 font-bold'
                                : ''
                          }
                        >
                          Parse
                        </span>
                        <span
                          className={
                            rfpJobStream.stage === 'chunking' ? 'text-blue-900 dark:text-blue-200 font-bold' : ''
                          }
                        >
                          Chunk
                        </span>
                        <span
                          className={
                            rfpJobStream.stage === 'indexing' ? 'text-blue-900 dark:text-blue-200 font-bold' : ''
                          }
                        >
                          Index
                        </span>
                        <span
                          className={
                            rfpJobStream.stage === 'extracting' || rfpJobStream.stage === 'processing'
                              ? 'text-blue-900 dark:text-blue-200 font-bold'
                              : ''
                          }
                        >
                          Extract
                        </span>
                        <span
                          className={
                            rfpJobStream.stage === 'complete' || rfpJobStream.stage === 'completed'
                              ? 'text-blue-900 dark:text-blue-200 font-bold'
                              : ''
                          }
                        >
                          Done
                        </span>
                      </div>
                      <div className="h-2.5 bg-blue-200 dark:bg-blue-800 rounded-full overflow-hidden shadow-inner">
                        <div
                          className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 transition-all duration-300 relative"
                          style={{
                            width: `${barWidth}%`,
                            minWidth: '8%',
                          }}
                        >
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-50 animate-shimmer"></div>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {isGeneratingTemplate ? (
                  <div className="mt-3 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                    <div className="flex items-center gap-2 mb-2">
                      <Loader2 className="h-4 w-4 animate-spin text-blue-600 dark:text-blue-400" />
                      <span className="text-sm font-semibold text-blue-900 dark:text-blue-200">
                        Generating Proposal Template
                      </span>
                    </div>
                    <p className="text-xs text-blue-700 dark:text-blue-300 mb-2">
                      Analyzing RFP documents and creating a customized template...
                    </p>
                    <ul className="text-xs text-blue-600 dark:text-blue-400 space-y-1 list-disc list-inside">
                      <li>Extracting RFP structure and requirements</li>
                      <li>Analyzing document sections and evaluation criteria</li>
                      <li>Generating comprehensive proposal questions</li>
                      <li>Validating question quality</li>
                    </ul>
                  </div>
                ) : rfpJobStream.stage === 'complete' && allDocumentsReadyForTemplate ? (
                  <div className="mt-3 flex items-center gap-2 text-xs text-green-700 bg-green-50 dark:bg-green-900/20 px-3 py-2 rounded-md">
                    <CheckCircle className="h-3 w-3" />
                    <span className="font-medium">Job stream finished and all listed documents are ready.</span>
                  </div>
                ) : rfpJobStream.stage === 'complete' && !allDocumentsReadyForTemplate ? (
                  <div className="mt-3 flex items-center gap-2 text-xs text-amber-800 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 rounded-md">
                    <AlertCircle className="h-3 w-3" />
                    <span className="font-medium">
                      Job reported complete, but some files in the list are still queued or processing — waiting on
                      the server.
                    </span>
                  </div>
                ) : rfpJobStream.stage === 'error' ? (
                  <div className="mt-3 flex items-center gap-2 text-xs text-red-700 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-md">
                    <AlertCircle className="h-3 w-3" />
                    <span className="font-medium">{rfpJobStream.error || 'Processing failed'}</span>
                  </div>
                ) : null}
              </>
            ) : (
              <div className="mt-3 space-y-2">
                {jobReportedCompleteRef.current ? (
                  <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 dark:bg-green-900/20 px-3 py-2 rounded-md">
                    <CheckCircle className="h-3 w-3" />
                    <span className="font-medium">Job stream finished — refreshing document statuses…</span>
                  </div>
                ) : (
                  <>
                    <div className="h-2.5 bg-blue-200 dark:bg-blue-800 rounded-full overflow-hidden shadow-inner">
                      <div
                        className="h-full w-2/5 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full animate-pulse"
                        style={{ animationDuration: '1.25s' }}
                      />
                    </div>
                    <p className="text-xs text-blue-600 dark:text-blue-400">
                      Live stream paused or finished early while ingest may still be running.
                      The list refreshes automatically.
                    </p>
                  </>
                )}
              </div>
            )}
          </div>
        )}


        {/* Actions */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          {showConfirmation && !documentsReadyForTemplate && artifactsListForTemplate.length > 0 ? (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  rfpJobStream.cancelStream();
                  setShowConfirmation(false);
                  setUploadedDocumentsInfo([]);
                  setAllArtifactsForTemplate([]);
                  setUploadedArtifactIds([]);
                  setUploadedJobId(null);
                  setJobDone(false);
                }}
              >
                Cancel
              </Button>
              <Button variant="primary" disabled>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Processing Documents...
              </Button>
            </>
          ) : isGenerating ? (
            <Button variant="outline" disabled className="w-full">
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Generating Template...
            </Button>
          ) : showConfirmation && files.length > 0 ? (
            // "Add more" (or any) files selected but not uploaded yet — must upload/index before Generate Template
            <>
              <Button variant="outline" onClick={() => setFiles([])} disabled={uploading}>
                Cancel
              </Button>
              <Button
                onClick={handleUpload}
                disabled={uploading}
                isLoading={uploading}
                variant="primary"
              >
                {uploading ? 'Uploading...' : `Upload ${files.length} File${files.length !== 1 ? 's' : ''}`}
              </Button>
            </>
          ) : showConfirmation && documentsReadyForTemplate ? (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  rfpJobStream.cancelStream();
                  setShowConfirmation(false);
                  setUploadedDocumentsInfo([]);
                  setAllArtifactsForTemplate([]);
                  setUploadedArtifactIds([]);
                  setUploadedJobId(null);
                  onClose();
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  const finalArtifactIds = completedArtifactsForTemplate.length > 0
                    ? completedArtifactsForTemplate.map((d) => d.artifact_id)
                    : artifactsListForTemplate.map((d) => d.artifact_id);
                  if (finalArtifactIds.length === 0) {
                    alert(
                      'No documents available for template generation. Please wait until all documents show Completed.'
                    );
                    return;
                  }
                  onUploadComplete(finalArtifactIds, uploadedJobId || '');
                }}
                variant="primary"
                disabled={isGenerating || !documentsReadyForTemplate}
              >
                {isGenerating ? 'Generating...' : 'Generate Template'}
              </Button>
            </>
          ) : rfpJobStream.isStreaming && !allDocumentsReadyForTemplate && !showConfirmation ? (
            <Button
              variant="outline"
              onClick={() => {
                rfpJobStream.cancelStream();
                onClose();
              }}
            >
              Cancel Processing
            </Button>
          ) : (
            // No job running - show upload button
            <>
              <Button variant="outline" onClick={onClose} disabled={uploading}>
                Cancel
              </Button>
              <Button
                onClick={handleUpload}
                disabled={files.length === 0 || uploading}
                isLoading={uploading}
              >
                {uploading ? 'Uploading...' : `Upload ${files.length} File${files.length !== 1 ? 's' : ''}`}
              </Button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}

/**
 * Format file size in bytes to human-readable format
 */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}
